import React from "react";
import { Link } from "react-router-dom";
import { Container } from "../ui/Container";
import { cn } from "../../utils/cn";
import { PARTNER_REGISTER_URL } from "../../utils/appLinks";

export interface FooterProps {
  className?: string;
}

const Footer: React.FC<FooterProps> = ({ className }) => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className={cn("relative overflow-hidden bg-[#0B0418] text-white pt-14 sm:pt-16 pb-10 sm:pb-12", className)}>
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-purple-400/40 to-transparent pointer-events-none" aria-hidden="true" />
      <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[720px] max-w-full h-[320px] bg-purple-700/20 rounded-full blur-3xl pointer-events-none" aria-hidden="true" />

      <Container size="xl" padding="md" className="relative z-10">
        {/* Main Footer Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-12 gap-8 sm:gap-10 lg:gap-12 pb-12 sm:pb-14 border-b border-purple-900/30">
          {/* Brand Info */}
          <div className="col-span-2 lg:col-span-6 space-y-4">
            <Link to="/" className="inline-flex items-center gap-2.5 group">
              <img
                src="/Logo1.png"
                alt="SetGo Logo"
                className="h-6 sm:h-7 w-auto object-contain drop-shadow-md group-hover:scale-105 transition-transform"
              />
            </Link>

            <p className="text-purple-200/70 text-xs sm:text-sm max-w-sm leading-relaxed">
              Discover nearby businesses, unlock offers and earn rewards every time you shop local.
            </p>

            <p className="text-xs text-purple-300/50 pt-2 font-mono">
              Customer care: support@setgoinnovations.com · Mon–Sat 9AM–7PM
            </p>
          </div>

          {/* Explore Links */}
          <div className="col-span-1 lg:col-span-2 space-y-3">
            <h4 className="text-xs font-bold uppercase tracking-wider text-purple-300">
              Explore
            </h4>
            <ul className="space-y-2 text-xs sm:text-sm text-purple-200/70">
              <li>
                <Link to="/deals" className="hover:text-white transition-colors">
                  Deals near you
                </Link>
              </li>
              <li>
                <Link to="/shops" className="hover:text-white transition-colors">
                  All Shops
                </Link>
              </li>
              <li>
                <Link to="/services" className="hover:text-white transition-colors">
                  Explore Services
                </Link>
              </li>
              <li>
                <a href="/#how-it-works" className="hover:text-white transition-colors">
                  How it works
                </a>
              </li>
            </ul>
          </div>

          {/* For Business */}
          <div className="col-span-1 lg:col-span-2 space-y-3">
            <h4 className="text-xs font-bold uppercase tracking-wider text-purple-300">
              For business
            </h4>
            <ul className="space-y-2 text-xs sm:text-sm text-purple-200/70">
              <li>
                <a
                  href={PARTNER_REGISTER_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-white transition-colors"
                >
                  List your business
                </a>
              </li>
              <li>
                <a
                  href="https://deals.setgoinnovations.com/business-owner/login"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-white transition-colors"
                >
                  Merchant login
                </a>
              </li>
            </ul>
          </div>

          {/* Company Links */}
          <div className="col-span-2 sm:col-span-1 lg:col-span-2 space-y-3">
            <h4 className="text-xs font-bold uppercase tracking-wider text-purple-300">
              Company
            </h4>
            <ul className="space-y-2 text-xs sm:text-sm text-purple-200/70">
              <li>
                <a href="/#about" className="hover:text-white transition-colors">
                  About us
                </a>
              </li>
              <li>
                <a href="mailto:contact@setgoinnovations.com" className="hover:text-white transition-colors">
                  Contact
                </a>
              </li>
              <li>
                <Link to="/privacy" className="hover:text-white transition-colors">
                  Privacy policy
                </Link>
              </li>
              <li>
                <Link to="/terms" className="hover:text-white transition-colors">
                  Terms of use
                </Link>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 flex flex-col md:flex-row items-center justify-between gap-3 md:gap-4 text-xs text-purple-300/60 text-center md:text-left">
          <div>
            &copy; {currentYear} SetGo. All rights reserved.
          </div>
          <div className="font-medium tracking-wide text-purple-300/80">
            Discover Local. Save More. Earn Rewards.
          </div>
          <div>
            Powered by{" "}
            <a
              href="https://xyvin.com"
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold text-purple-200 hover:text-white underline-offset-4 hover:underline transition-colors"
            >
              Xyvin Technologies
            </a>
          </div>
        </div>
      </Container>
    </footer>
  );
};

export { Footer };
export default Footer;
